import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Orbit, Cpu, ScanLine, Activity,
  Building2, Gauge, Wrench, Layers, ShieldCheck, MessageCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import Header from '../Header';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import SEO from '../SEO';

const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/33d0bc9d16a3b720d7d3814c480c86068cac28b7eb2e19300c0bfffc4652055e.jpeg',
  twin: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/fae77ea0e9c46065796852404b5114a430107a150441f93c2418e25437c5f2b0.jpeg',
  iot: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/aad82d225f58344974e5f76a0549e2fa8a697051cae63863b9dbe4e0d95c347f.jpeg',
  asbuilt: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/64dadb73311f56a4a214d448b7f365d953d183ce0917e4b864b628169ac7ad2e.jpeg',
};

const WHATSAPP_URL = 'https://wa.me/5561995792667?text=' + encodeURIComponent('Olá! Tenho interesse em Gêmeos Digitais, IoT e As Built 3D (AJ | BIM Digital Twin).');

const pillars = [
  {
    id: 'digital-twin',
    icon: Orbit,
    tag: 'Gêmeos Digitais',
    title: 'Gêmeos Digitais (Digital Twin)',
    image: IMAGES.twin,
    text: 'Uma réplica digital, viva e conectada, do seu edifício ou ativo. Integramos o modelo BIM aos dados operacionais para simular cenários, antecipar falhas e apoiar decisões ao longo de todo o ciclo de vida — do projeto à operação e manutenção.',
    bullets: ['Modelo 3D sincronizado com a realidade', 'Simulação de cenários e manutenção preditiva', 'Painéis de dados em tempo real'],
  },
  {
    id: 'iot',
    icon: Cpu,
    tag: 'IoT',
    title: 'IoT — Internet das Coisas',
    image: IMAGES.iot,
    text: 'Sensores conectados capturam temperatura, energia, ocupação, vibração e muito mais. Esses dados alimentam o gêmeo digital e transformam o edifício em um ativo inteligente, monitorado 24/7.',
    bullets: ['Sensores integrados ao modelo BIM', 'Monitoramento de energia e conforto', 'Alertas automáticos e eficiência operacional'],
    reverse: true,
  },
  {
    id: 'as-built',
    icon: ScanLine,
    tag: 'As Built 3D',
    title: 'As Built 3D & Reality Capture',
    image: IMAGES.asbuilt,
    text: 'Com escaneamento a laser e nuvem de pontos, capturamos a condição real construída e geramos modelos As Built precisos. A base perfeita para reformas, retrofit e para dar vida ao gêmeo digital.',
    bullets: ['Escaneamento a laser e nuvem de pontos', 'Modelos As Built fiéis à obra', 'Documentação precisa para retrofit'],
  },
];

const benefits = [
  { icon: Gauge, title: 'Operação em tempo real', text: 'Acompanhe o desempenho do ativo com dados vivos e indicadores acionáveis.' },
  { icon: Wrench, title: 'Manutenção preditiva', text: 'Antecipe falhas e reduza custos com intervenções planejadas.' },
  { icon: Activity, title: 'Eficiência energética', text: 'Monitore e otimize o consumo de energia e o conforto dos ambientes.' },
  { icon: Layers, title: 'Ciclo de vida completo', text: 'Um único modelo do projeto à operação, integrando todas as disciplinas.' },
  { icon: Building2, title: 'Gestão de ativos', text: 'Centralize informações de equipamentos, garantias e histórico de manutenção.' },
  { icon: ShieldCheck, title: 'Decisões seguras', text: 'Reduza riscos com simulações e uma base de dados confiável e auditável.' },
];

const DigitalTwinPage = () => {
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-black" data-testid="digital-twin-page">
      <SEO
        title="AJ | BIM Digital Twin - Gêmeos Digitais, IoT e As Built 3D"
        description="Gêmeos Digitais (Digital Twin), IoT e As Built 3D para operar, monitorar e otimizar seus ativos em tempo real. Reality capture, nuvem de pontos e integração BIM."
        keywords="gêmeo digital, digital twin, IoT, as built 3D, nuvem de pontos, reality capture, BIM, manutenção preditiva"
      />
      <Header onLoginClick={() => setIsLoginModalOpen(true)} />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />

      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <img src={IMAGES.hero} alt="AJ | BIM Digital Twin" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-cyan-950/60" />
        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-cyan-500/15 text-cyan-300 border-cyan-500/30 mb-5" data-testid="dt-hero-badge">
              <Orbit className="w-3.5 h-3.5 mr-1.5" />
              AJ | BIM Digital Twin
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Gêmeos Digitais, <span className="text-cyan-400">IoT</span> e <span className="text-cyan-400">As Built 3D</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl">
              Transformamos seus modelos BIM em ativos inteligentes e conectados. Monitore, simule e
              opere edifícios e infraestruturas em tempo real — com dados que vivem junto do seu projeto.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="dt-hero-cta">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold h-12 px-7">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Falar com um especialista
                </Button>
              </a>
              <Button
                variant="outline"
                onClick={() => document.getElementById('dt-pilares')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 h-12 px-7"
                data-testid="dt-hero-explore"
              >
                Como funciona
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-black border-t border-zinc-900">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-3xl">
          <h2 className="text-3xl font-bold text-white mb-4">O seu edifício, agora inteligente</h2>
          <p className="text-gray-400 text-base md:text-lg">
            Um gêmeo digital conecta o mundo físico ao digital: sensores IoT capturam o que acontece na obra
            e na operação, o As Built 3D garante fidelidade à realidade e o modelo BIM une tudo em uma única
            fonte de verdade — inteligente, viva e acessível de qualquer lugar.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section id="dt-pilares" className="py-8 bg-black">
        <div className="container mx-auto px-4 lg:px-8 space-y-16 md:space-y-24">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center ${p.reverse ? 'md:[direction:rtl]' : ''}`}
                data-testid={`dt-pillar-${p.id}`}
              >
                <div className="[direction:ltr]">
                  <div className="relative rounded-2xl overflow-hidden border border-cyan-500/20 shadow-[0_0_40px_-15px_rgba(34,211,238,0.4)]">
                    <img src={p.image} alt={p.title} className="w-full h-[280px] md:h-[340px] object-cover" />
                  </div>
                </div>
                <div className="[direction:ltr]">
                  <Badge className="bg-cyan-500/15 text-cyan-300 border-cyan-500/30 mb-4">
                    <Icon className="w-3.5 h-3.5 mr-1.5" />
                    {p.tag}
                  </Badge>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{p.title}</h3>
                  <p className="text-gray-400 mb-6">{p.text}</p>
                  <ul className="space-y-3">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start text-gray-300">
                        <span className="mt-1 mr-3 w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-b from-black to-cyan-950/20 border-t border-zinc-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3">Por que investir em um Gêmeo Digital?</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Benefícios reais para quem projeta, constrói e opera edifícios e infraestruturas.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 hover:border-cyan-500/40 transition-colors"
                  data-testid={`dt-benefit-${b.title}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{b.title}</h3>
                  <p className="text-gray-400 text-sm">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="relative max-w-4xl mx-auto rounded-3xl overflow-hidden border border-cyan-500/30 bg-gradient-to-br from-cyan-900/30 via-zinc-900 to-black p-10 md:p-14 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto para dar vida ao seu ativo?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Fale com nossa equipe e descubra como aplicar Gêmeos Digitais, IoT e As Built 3D
              no seu projeto ou operação.
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="dt-cta-whatsapp">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold h-12 px-8">
                <MessageCircle className="mr-2 w-5 h-5" />
                Solicitar contato
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Voltar */}
      <section className="py-12 bg-black border-t border-zinc-900">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
            data-testid="dt-back-btn"
          >
            <ArrowLeft className="mr-2" size={18} />
            Voltar ao Portal
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalTwinPage;
